import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Layout from 'components/layout';
import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';
import ImageHero from 'components/hero/imagehero';
import {
  ImageSlider,
  TitleText,
  HouseTypeSlideshow,
  TitleCaptionText,
  ImageGrid,
  Downloads,
  Location,
  Availability,
  RegisterInterest,
  SchemeDetails,
  FullImage,
} from 'components/slices';
import Spacer from 'components/spacer';
import { withStyles } from '@material-ui/styles';
import Logo from 'images/Hayfield-Wordmark.svg';
import { LazyLoadComponent } from 'react-lazy-load-image-component';
import linkResolver from 'utils/linkResolver';
import { AnchorLink } from 'gatsby-plugin-anchor-links';
import { Helmet } from 'react-helmet';

import Hidden from '@material-ui/core/Hidden';
import ReactPlayer from 'react-player';

// Query for the Blog Post content in Prismic
export const query = graphql`
  query DevelopmentPageQuery($uid: String) {
    prismic {
      allDevelopments(uid: $uid) {
        edges {
          node {
            _meta {
              id
              uid
              type
            }
            metadata {
              title
              keywords
              description
            }
            title
            telephone
            email
            opening_hours
            additional_info
            geolocation
            banner_image
            banner_image_tablet
            banner_image_mobile
            location_image
            address {
              address_line_1
              address_line_2
              county
              postcode
              town
              local_area_guide {
                ... on PRISMIC__FileLink {
                  url
                }
              }
            }
            introduction
            prices_from
            brochure {
              ... on PRISMIC__FileLink {
                url
                name
              }
            }
            promotion_banners {
              image
            }
            main_page_slideshow {
              image
              title
            }
            available_house_types {
              home_type {
                ... on PRISMIC_Home_type {
                  title
                  _meta {
                    uid
                    type
                    id
                  }
                  number_of_bedrooms
                  thumbnail_image
                }
              }
            }
            the_details {
              text
              title
            }
            video
            details_images {
              tile1_image
              tile2_image
              tile3_image
              tile4_image
              tile5_image
              tile6_image
              tile7_image
            }
            download_block_content {
              image
              text
              title
            }
            downloads {
              download_item {
                ... on PRISMIC__FileLink {
                  _linkType
                  url
                }
              }
              title
            }
            location_details {
              caption
              text
              title
            }
            location_images {
              title
              image
            }
            available_house_types {
              home_type {
                ... on PRISMIC_Home_type {
                  title
                  price_from
                  number_of_bedrooms
                  homes_available
                  available_plots
                }
              }
            }
            scheme_details {
              title
              logo_image
              image
              link {
                ... on PRISMIC_Basic_page {
                  _meta {
                    uid
                    type
                    id
                  }
                }
                ... on PRISMIC_Article {
                  _meta {
                    id
                    type
                    uid
                  }
                }
                ... on PRISMIC_Standard_page {
                  _meta {
                    id
                    type
                    uid
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

const styles = theme => ({
  logo: {
    width: '80px',
    [theme.breakpoints.up('lg')]: {
      width: '100px',
    },
  },
  btnLink: {
    display: 'inline-block',
    cursor: 'pointer',
    border: `3px solid ${theme.palette.primary.main}`,
    background: theme.palette.primary.main,
    color: theme.palette.text.white,
    padding: '5px 20px',
    textTransform: 'uppercase',
    textDecoration: 'none',
    boxShadow: `inset 0px 0px 0px 0px ${theme.palette.text.white}`,
    transition: 'box-shadow 0.2s ease-in-out',
    [theme.breakpoints.up('md')]: {
      padding: '8px 20px',
    },
    '&:hover': {
      boxShadow: `inset 0px 0px 0px 3px ${theme.palette.text.white}`,
    },
  },
  introWrapper: {
    textAlign: 'center',
    [theme.breakpoints.up('lg')]: {
      textAlign: 'left',
    },
  },
  videoWrapper: {
    position: 'relative',
    paddingTop: '56.25%',
    width: '100%',
    '& iframe': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100% !important',
      height: '100% !important',
    },
  },
});

const hasAvailableHomes = data => {
  var retval = false;
  data.map(item => {
    if (item.home_type.homes_available === true) {
      retval = true;
    }
  });
  return retval;
};

const Development = props => {
  const { classes } = props;
  const doc = props.data.prismic.allDevelopments.edges.slice(0, 1).pop();
  if (!doc) return null;
  return (
    <Layout
      developmentPageMenu={
        doc.node.available_house_types &&
        doc.node.available_house_types[0] &&
        doc.node.available_house_types[0].home_type
          ? doc.node._meta
          : ''
      }
    >
      {doc.node.metadata && doc.node.metadata[0] && (
        <Helmet>
          {doc.node.metadata[0].title && doc.node.metadata[0].title[0] && (
            <title>{doc.node.metadata[0].title[0].text}</title>
          )}
          {doc.node.metadata[0].description &&
            doc.node.metadata[0].description[0] && (
              <Helmet>
                <meta
                  name="description"
                  content={doc.node.metadata[0].description[0].text}
                />
                <meta
                  property="og:description"
                  content={doc.node.metadata[0].description[0].text}
                />
                <meta
                  name="twitter:description"
                  content={doc.node.metadata[0].description[0].text}
                />
              </Helmet>
            )}
          {doc.node.metadata[0].keywords &&
            doc.node.metadata[0].keywords[0] && (
              <meta
                name="keywords"
                content={doc.node.metadata[0].keywords[0].text}
              />
            )}
        </Helmet>
      )}
      <ImageHero
        data={doc.node.banner_image ? doc.node.banner_image : ''}
        banner_image_tablet={doc.node.banner_image_tablet || null}
        banner_image_mobile={doc.node.banner_image_mobile || null}
        title={doc.node.title[0].text}
        brochure={doc.node.brochure ? doc.node.brochure.url : ''}
        email={doc.node.email ? doc.node.email[0].text : ''}
        telephone={doc.node.telephone ? doc.node.telephone[0].text : ''}
        opening_hours={
          doc.node.opening_hours ? doc.node.opening_hours[0].text : ''
        }
        additional_info={
          doc.node.additional_info ? doc.node.additional_info[0].text : ''
        }
        devName={doc.node?.title[0]?.text}
        contactbar
        logo
        development={true}
      />
      <Container maxWidth="xl">
        <Spacer>
          <Grid container>
            <Box
              component={Grid}
              item
              xs={12}
              lg={6}
              display="flex"
              alignItems="center"
              justifyContent="start"
              flexDirection="column"
              mb={[4, 4, 4, 0]}
            >
              <Logo className={classes.logo} />
              <Box mb={2}>
                <Typography component="h2" variant="h1">
                  {doc.node.title[0].text}
                </Typography>
              </Box>
              <Typography component="p" variant="subtitle1" align="center">
                {doc.node.address
                  ? doc.node.address[0].address_line_1
                    ? doc.node.address[0].address_line_1[0].text
                    : null
                  : null}
                ,{' '}
                {doc.node.address
                  ? doc.node.address[0].address_line_2
                    ? doc.node.address[0].address_line_2[0].text
                    : null
                  : null}
              </Typography>
              <Typography component="p" variant="subtitle1" align="center">
                {doc.node.address[0].town[0].text},{' '}
                {doc.node.address[0].county[0].text},{' '}
                {doc.node.address[0].postcode[0].text}
              </Typography>
              {doc.node.location_image && doc.node.location_image.url && (
                <Box mt={4} width="60%">
                  <img
                    src={`/.netlify/functions/image-proxy?url=${doc.node.location_image.url}&q=10&lossless=1`}
                    alt={doc.node.location_image.alt}
                    className="lazyload"
                  />
                </Box>
              )}
            </Box>

            <Box
              component={Grid}
              item
              xs={12}
              lg={5}
              display={{ xs: 'flex', lg: 'block' }}
              flexDirection="column"
              alignItems="center"
              className={classes.introWrapper}
            >
              <Typography component="p" variant="body1" gutterBottom>
                {doc.node.introduction[0].text}
              </Typography>
              {doc.node.prices_from &&
                doc.node.prices_from[0] &&
                doc.node.prices_from[0].text && (
                  <Box pt={2}>
                    <Typography component="p" variant="h6">
                      {!isNaN(doc.node.prices_from[0].text)
                        ? 'Prices from'
                        : ''}{' '}
                      {doc.node.prices_from[0].text}
                    </Typography>
                    {doc.node.available_house_types &&
                      doc.node.available_house_types[0] &&
                      doc.node.available_house_types[0].home_type && (
                        <Box mt={2}>
                          <AnchorLink
                            to={`${linkResolver(doc.node._meta)}#availability`}
                            title="Availability"
                            className={`${classes.btnLink} stripped`}
                            stripHash
                          >
                            <Typography component="span" variant="button">
                              View Availability
                            </Typography>
                          </AnchorLink>
                        </Box>
                      )}
                  </Box>
                )}
            </Box>
          </Grid>
        </Spacer>
        {doc.node.promotion_banners && doc.node.promotion_banners[0] && (
          <React.Fragment>
            {doc.node.promotion_banners.map((item, key) => {
              if (item.image) {
                return (
                  <Spacer key={key}>
                    <FullImage data={item} />
                  </Spacer>
                );
              }
            })}
          </React.Fragment>
        )}
        {doc.node.main_page_slideshow &&
          doc.node.main_page_slideshow[0] &&
          doc.node.main_page_slideshow[0].image &&
          doc.node.main_page_slideshow.length > 0 && (
            <Spacer>
              <ImageSlider data={doc.node.main_page_slideshow} />
            </Spacer>
          )}

                  {doc.node.video && doc.node.video.embed_url && (
          <Spacer>
            {doc.node.video.html ? (
              <div
                className={classes.videoWrapper}
                dangerouslySetInnerHTML={{ __html: doc.node.video.html }}
              />
            ) : (
              <div className={classes.videoWrapper}>
                <ReactPlayer
                  style={{ position: 'absolute', top: 0, left: 0 }}
                  url={doc.node.video.embed_url}
                  controls={true}
                  width="100%"
                  height="100%"
                />
              </div>
            )}
          </Spacer>
        )}

        {doc.node.the_details && doc.node.the_details[0].title && (
          <Spacer>
            {doc.node.the_details[0].title && doc.node.the_details[0].text && (
              <TitleText data={doc.node.the_details[0]} />
            )}
            <Hidden xsDown>
              {doc.node.details_images[0].tile1_image && (
                <Spacer>
                  <ImageGrid data={doc.node.details_images} />
                </Spacer>
              )}
            </Hidden>
          </Spacer>
        )}
      </Container>

      {doc.node.available_house_types &&
        doc.node.available_house_types[0] &&
        doc.node.available_house_types[0].home_type && (
          <Box id="housetypes">
            <Spacer>
              <HouseTypeSlideshow
                data={doc.node.available_house_types}
                development_name={doc.node.title[0].text}
                brochureUrl={doc.node.brochure.url}
                downloads={doc.node.downloads}
                downloadsContent={doc.node.download_block_content[0]}
              />
            </Spacer>
          </Box>
        )}
      <Container maxWidth="xl">
        {doc.node.downloads &&
          doc.node.downloads[0] &&
          doc.node.downloads[0].title && (
            <Box id="siteplan">
              <Spacer>
                <Downloads
                  data={doc.node.downloads}
                  content={doc.node.download_block_content[0]}
                />
              </Spacer>
            </Box>
          )}
        {doc.node.location_details &&
          doc.node.location_details[0] &&
          doc.node.location_details[0].title &&
          doc.node.location_details[0].title[0] &&
          doc.node.location_details[0].title[0].text && (
            <Spacer>
              <TitleCaptionText data={doc.node.location_details[0]} />
            </Spacer>
          )}
        {doc.node.location_images &&
          doc.node.location_images[0] &&
          doc.node.location_images[0].image && (
            <React.Fragment>
              {doc.node.location_images[0].image && (
                <Spacer>
                  <ImageSlider data={doc.node.location_images} />
                </Spacer>
              )}
            </React.Fragment>
          )}
        {doc.node.address && doc.node.geolocation && (
          <Spacer>
            <Location
              title={doc.node.title[0].text}
              data={doc.node.address[0]}
              geolocation={doc.node.geolocation}
              locationPostcode={doc.node.address[0].postcode[0].text}
              localareaguide={
                doc.node.address[0].local_area_guide
                  ? doc.node.address[0].local_area_guide.url
                  : ''
              }
            />
          </Spacer>
        )}
      </Container>
      <Container maxWidth="lg">
        {doc.node.available_house_types &&
          doc.node.available_house_types[0] &&
          doc.node.available_house_types[0].home_type && (
            <React.Fragment>
              {hasAvailableHomes(doc.node.available_house_types) && (
                <Box id="availability">
                  <Spacer>
                    <Availability data={doc.node.available_house_types} />
                  </Spacer>
                </Box>
              )}
            </React.Fragment>
          )}

        <Spacer>
          <RegisterInterest
            telephone={doc.node.telephone ? doc.node.telephone[0].text : ''}
            email={
              doc.node.email
                ? doc.node.email[0].text
                : process.env.DEFAULT_EMAIL
            }
          />
        </Spacer>

        {doc.node.scheme_details.length > 0 &&
          doc.node.scheme_details[0].image && (
            <Spacer>
              <SchemeDetails data={doc.node.scheme_details} />
            </Spacer>
          )}
      </Container>
    </Layout>
  );
};

Development.propTypes = {
  data: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(Development);
